import React, { useState } from 'react';
import {
    Typography,
    Button,
    Box,
    Paper,
    FormControl,
    TextField,
    Autocomplete,
    CircularProgress,
    Alert,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    ToggleButton,
    ToggleButtonGroup,
    Snackbar
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import apiService from '../services/api';

const Testimonials = ({
    brands,
    categories,
    brand,
    category,
    year,
    country,
    onBrandChange,
    onCategoryChange,
    loadingData,
    error,
    setError
}) => {
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [order, setOrder] = useState('longest');
    const [testimonials, setTestimonials] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasSearched, setHasSearched] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [snackbar, setSnackbar] = useState('');

    const fetchTestimonials = async (selectedOrder = order) => {
        setLoading(true);
        setError('');
        try {
            const response = await apiService.getTestimonials({
                country,
                year,
                brand: brand || null,
                category: category || null,
                order: selectedOrder,
            });
            setTestimonials(response.data.testimonials || []);
            setTotal(response.data.total || 0);
            setHasSearched(true);
            setPage(0);
        } catch (err) {
            console.error('Error fetching testimonials:', err);
            setError('Failed to load testimonials. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleOrderChange = (event, newOrder) => {
        if (!newOrder) return;
        setOrder(newOrder);
        if (hasSearched) {
            fetchTestimonials(newOrder);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        setError('');
        try {
            const filename = await apiService.exportTestimonials({
                country,
                year,
                brand: brand || null,
                category: category || null,
                order,
            });
            setSnackbar(`Downloaded ${filename}`);
        } catch (err) {
            console.error('Error exporting testimonials:', err);
            setError('Failed to export testimonials. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const paginated = testimonials.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Testimonials
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Browse and export the open-ended consumer trust responses ("testimonials"),
                ranked by length. Filter by brand and category, then download a CSV to share
                with the brand.
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <FormControl sx={{ minWidth: 200, flex: 1 }}>
                        <Autocomplete
                            value={brand || null}
                            onChange={(event, newValue) => onBrandChange(newValue || '')}
                            options={brands}
                            renderInput={(params) => <TextField {...params} label="Brand (Optional)" />}
                            disabled={loadingData || loading}
                        />
                    </FormControl>

                    <FormControl sx={{ minWidth: 200, flex: 1 }}>
                        <Autocomplete
                            value={category || null}
                            onChange={(event, newValue) => onCategoryChange(newValue || '')}
                            options={categories}
                            renderInput={(params) => <TextField {...params} label="Category (Optional)" />}
                            disabled={loadingData || loading}
                        />
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        value={order}
                        exclusive
                        onChange={handleOrderChange}
                        size="small"
                        disabled={loading}
                    >
                        <ToggleButton value="longest">Longest first</ToggleButton>
                        <ToggleButton value="shortest">Shortest first</ToggleButton>
                    </ToggleButtonGroup>

                    <Button
                        variant="contained"
                        onClick={() => fetchTestimonials()}
                        disabled={loading || loadingData}
                        sx={{ minWidth: 160 }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Load Testimonials'}
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
                        onClick={handleExport}
                        disabled={exporting || loadingData}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {hasSearched && !loading && (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1">
                            {total} testimonial{total === 1 ? '' : 's'}
                        </Typography>
                        {brand && <Chip label={brand} size="small" />}
                        {category && <Chip label={category} size="small" variant="outlined" />}
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {total === 0 ? (
                        <Alert severity="info">
                            No testimonials found for the selected filters.
                        </Alert>
                    ) : (
                        <Paper>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Brand</TableCell>
                                            <TableCell>Category</TableCell>
                                            <TableCell>Testimonial</TableCell>
                                            <TableCell align="right">Length</TableCell>
                                            <TableCell>Trust Depth</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginated.map((t, idx) => (
                                            <TableRow key={page * rowsPerPage + idx} hover>
                                                <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                                                <TableCell>{t.brand}</TableCell>
                                                <TableCell>{t.category}</TableCell>
                                                <TableCell sx={{ maxWidth: 500, whiteSpace: 'pre-wrap' }}>
                                                    {t.reason}
                                                </TableCell>
                                                <TableCell align="right">{t.length}</TableCell>
                                                <TableCell>{t.trust_depth}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(event, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setRowsPerPage(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </Paper>
                    )}
                </Box>
            )}

            <Snackbar
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </Box>
    );
};

export default Testimonials;
